import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { SubmitHandler } from 'redux-form';
import { Link, useLocation, useHistory, useParams } from 'react-router-dom';
import { reduxForm, Field, getFormValues, change } from 'redux-form/immutable';
import { useSelector, useDispatch } from 'react-redux';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Divider from '@material-ui/core/Divider';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import FormHelperText from '@mui/material/FormHelperText';
import { CircularProgress, Typography } from '@mui/material';

import config from 'config';
import SplashWrapper from 'retired/SplashWrapper';
import FormTextField from 'retired/FormTextField';
import * as Validators from 'utils/validators';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import TermsOfService from 'components/TermsOfService';
import { useQueryParams } from 'utils/useQueryParams';
import api from 'entities/api';

import reducer from './reducer';
import epics from './epics';
import { resetLoginAction, loginAction } from './actions';
import { selectLoginRejected } from './selectors';
import { useSsoCompany } from './useSsoCompany';

const required = Validators.required();
const isEmail = Validators.isEmail();

const key = 'loginPage';

interface LoginPageProps {
  invalid: boolean;
  handleSubmit: SubmitHandler<{}, {}, string>;
}

const styles: Partial<{ [key: string]: React.CSSProperties }> = {
  flexContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
};

const LoginPage: React.FC<LoginPageProps> = (props) => {
  const { invalid, handleSubmit } = props;
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const loginRejected = useSelector(selectLoginRejected) as boolean;

  api.LOGGING_OUT = false;

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    dispatch(resetLoginAction());
  }, []);

  const formValues = useSelector(getFormValues('login'));

  const emailFieldValue = (formValues?.get?.('email') as string) || '';

  const { companyId } = useParams<{ companyId: string }>();
  const queryParams = useQueryParams();
  const errorReference = queryParams.get('errorReference') || undefined;

  const d = queryParams.get('d') || '';

  const userSsoCompany = useSsoCompany(emailFieldValue, companyId);

  const isSsoLogin = companyId || userSsoCompany || errorReference;

  const submit = (data: any) => {
    if (
      userSsoCompany &&
      userSsoCompany.sso2?.provider &&
      userSsoCompany.sso2?.identifier &&
      data.get('email')
    ) {
      const { provider, identifier } = userSsoCompany.sso2;

      window.location.href = `${
        config.apiUrl
      }/auth/${provider}/${identifier}?type=Organizer&username=${encodeURIComponent(
        data.get('email')
      )}${d ? `&d=${d}` : ''}`;
    } else {
      dispatch(loginAction(data.get('email'), data.get('password')));
    }
  };

  return (
    <SplashWrapper logo={userSsoCompany ? userSsoCompany.mainPicture : undefined}>
      <Helmet title="Login" />
      <Typography variant={isSsoLogin ? 'h3' : 'h1'} mb={5}>
        {isSsoLogin ? 'Single sign-on' : 'Organizer Login'}
      </Typography>
      {isSsoLogin && (
        <Typography variant="h5" mb={5}>
          Please enter your company email.
        </Typography>
      )}
      <form onSubmit={handleSubmit(submit)}>
        <Field
          name="email"
          type="email"
          label="Email"
          component={FormTextField}
          fullWidth
          autoFocus
          style={{ marginBottom: errorReference ? 8 : 20 }}
          validate={[required, isEmail]}
        />
        {errorReference && (
          <FormHelperText error style={{ marginBottom: 20, fontSize: '0.8rem' }}>
            An error occurred authenticating through SSO, please try again or contact support. Ref:
            #{errorReference}
          </FormHelperText>
        )}
        {!isSsoLogin && (
          <Field
            name="password"
            type="password"
            label="Password"
            fullWidth
            component={FormTextField}
            style={{ marginBottom: 50 }}
            validate={required}
          />
        )}
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={invalid || (companyId && !userSsoCompany)}
          style={{ width: '100%' }}
        >
          {userSsoCompany ? (
            `${userSsoCompany.name} Company log in`
          ) : companyId ? (
            <CircularProgress size={21} />
          ) : (
            'Login'
          )}
        </Button>

        <div style={{ ...styles.flexContainer, marginTop: '1em', textAlign: 'center' }}>
          <TermsOfService />
        </div>

        {isSsoLogin ? (
          <div style={{ ...styles.flexContainer }}>
            <Button
              size="large"
              variant="text"
              onClick={() => {
                dispatch(change('login', 'email', ''));
                history.replace('/login');
              }}
              style={{ marginTop: 24, textDecoration: 'underline', fontSize: 16 }}
            >
              Log in without Single sign-on
            </Button>
          </div>
        ) : (
          <>
            <Divider style={{ margin: '1em 0px .5em 0px' }} />
            <div style={{ ...styles.flexContainer }}>
              <p>
                Don&apos;t have an account? <Link to="/register">Register here!</Link>
              </p>
            </div>

            <div style={{ ...styles.flexContainer }}>
              <Link to="/forgot">Forgot password?</Link>
            </div>
          </>
        )}

        <Dialog open={loginRejected} onClose={() => dispatch(resetLoginAction())}>
          <DialogTitle>Failed to Log In!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              There was an issue with your login. Your user might still be pending review by Deed
              staff, your password might be incorrect, or your email hasn&apos;t been verified yet.
              Try setting a new password to unblock your account.{' '}
              {!isSsoLogin && <Link to="/forgot">Forgot password?</Link>}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Link to={`/login${location.search}`}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => dispatch(resetLoginAction())}
              >
                Close
              </Button>
            </Link>
          </DialogActions>
        </Dialog>
      </form>
    </SplashWrapper>
  );
};

const withForm = reduxForm({ form: 'login' });

export default withForm(LoginPage);
